import { CheckCircle, Lightbulb, AlertCircle } from "lucide-react";

interface LessonContentProps {
  content: string;
}

export const LessonContent = ({ content }: LessonContentProps) => {
  // Split content into sections and format them
  const formatContent = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip empty lines
      if (line.trim() === '') {
        elements.push(<div key={key++} className="h-4" />);
        continue;
      }

      // Bold headers (lines starting with **)
      if (line.match(/^\*\*[^*]+\*\*:?$/)) {
        const text = line.replace(/^\*\*|\*\*:?$/g, '');
        elements.push(
          <h3 key={key++} className="text-lg font-semibold text-primary mt-6 mb-3 flex items-center gap-2">
            <div className="w-1 h-6 bg-accent rounded-full" />
            {text}
          </h3>
        );
        continue;
      }

      // Numbered points (like "1. Hot Flashes")
      if (line.match(/^\*\*\d+\./)) {
        const parts = line.split('**');
        const number = parts[1];
        const rest = parts.slice(2).join('').trim();
        elements.push(
          <div key={key++} className="bg-primary-soft/20 rounded-lg p-4 my-3 border-l-4 border-primary">
            <h4 className="font-semibold text-foreground text-base mb-1 flex items-center gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                {number.replace(/\D/g, '')}
              </span>
              {number.replace(/^\d+\./, '').trim()}
            </h4>
            <p className="text-muted-foreground ml-8">{rest}</p>
          </div>
        );
        continue;
      }

      // Bullet points starting with "- "
      if (line.match(/^-\s+/)) {
        const text = line.replace(/^-\s+/, '');
        // Check if it has bold text
        if (text.includes('**')) {
          const parts = text.split('**');
          elements.push(
            <div key={key++} className="flex items-start gap-3 my-2 ml-4">
              <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-foreground">
                <span className="font-semibold text-primary">{parts[1]}</span>
                {parts.length > 2 && `: ${parts[2]}`}
              </p>
            </div>
          );
        } else {
          elements.push(
            <div key={key++} className="flex items-start gap-3 my-2 ml-4">
              <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-foreground">{text}</p>
            </div>
          );
        }
        continue;
      }

      // Important notes (lines starting with "Important:" or "Remember:")
      if (line.match(/^\*\*(Important|Remember|Note):/)) {
        const text = line.replace(/^\*\*|\*\*/g, '');
        elements.push(
          <div key={key++} className="bg-accent-soft/60 border-l-4 border-accent rounded-lg p-4 my-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-foreground font-medium">{text}</p>
          </div>
        );
        continue;
      }

      // Regular paragraphs
      if (line.trim().length > 0) {
        // Handle inline bold text
        if (line.includes('**')) {
          const parts = line.split('**');
          elements.push(
            <p key={key++} className="text-foreground leading-relaxed my-3">
              {parts.map((part, idx) => 
                idx % 2 === 1 ? <strong key={idx} className="font-semibold text-primary">{part}</strong> : part
              )}
            </p>
          );
        } else {
          elements.push(
            <p key={key++} className="text-foreground leading-relaxed my-3">
              {line}
            </p>
          );
        }
      }
    }

    return elements;
  };

  return (
    <div className="bg-gradient-to-br from-card via-background to-primary-soft/5 rounded-xl p-6 md:p-8 shadow-soft border-2 border-primary/10">
      <div className="max-w-none">
        {formatContent(content)}
      </div>
    </div>
  );
};